import React, { useContext, useEffect, useState } from 'react';
import { cn } from '../lib/utils';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { AlertTriangle, ArrowLeft, Calculator, Info } from 'lucide-react';
import { RESCUE_REASONS } from '../utils/rescueDocsCatalog';
import createWithdrawalRequest from '../api/pension-plan-withdrawals/createWithdrawalRequest';
import {
	WithdrawalKind,
	WithdrawalReason
} from '../api/pension-plan-withdrawals/interfaces/WithdrawalRequest';
import { fmtEur, fmtNum } from '../utils/formatArcaNumbers';
import {
	MIN_MONTHLY_RENTA,
	RENTA_MENSUAL_DAY,
	RENTA_ALERT_WINDOW_DAYS,
	daysUntilNextRentaDay
} from './MiBeneficioV2/ConfigurarRentaMensualDialogV2';
import RescueTaxSimulatorV2 from './SimulatorsV2/RescueTaxSimulatorV2';
import RescueRequestSubmittedV2 from './RescueRequestSubmittedV2';
import { translateWithdrawalError } from '../utils/translateWithdrawalError';
import RescueSupportNoteV2 from './RescueSupportNoteV2';

/**
 * ⚠️ UX SCAFFOLD (tarea Notion "Definimos MVP rescates") — valida el flujo de
 * SOLICITAR (o CAMBIAR) un motivo de rescate (a diferencia de
 * `RescueRequestDialogV2`/`ConfigurarRentaMensualDialogV2`, que son para
 * alguien que YA tiene el motivo establecido y solo gestiona un rescate en
 * curso). Un único paso: motivo + configuración del rescate (tipo
 * puntual/renta y sus campos), todo junto — elegir motivo y configurar el
 * rescate son parte de la misma decisión, no tiene sentido separarlas. Al
 * enviar, pasa directo a la confirmación (`RescueRequestSubmittedV2`), sin
 * paso de KYC ni de contraseña (quitados a petición de usuario, jul-2026 —
 * quedará para una tarea de seguridad futura): la propia confirmación ya
 * avisa de que el equipo contactará para pedir la documentación necesaria,
 * así que no hace falta pedirla ni verificar identidad aquí. SIN IBAN aquí
 * a propósito, mismo criterio que `RescueRequestDialogV2`/
 * `ConfigurarRentaMensualDialogV2`: se pedirá más adelante, no en este modal.
 *
 * Tipo de rescate: puntual pide solo la cantidad (mismo campo que
 * `RescueRequestDialogV2`, sin el IBAN); renta mensual pide cantidad/mes +
 * día (mismos campos que `ConfigurarRentaMensualDialogV2`, también sin el
 * IBAN ni la pausa, que no aplican a una solicitud nueva). "Renta mensual" se
 * deshabilita cuando el motivo elegido es uno de los "supuestos
 * excepcionales de liquidez" (enfermedad grave, desempleo de larga duración
 * — RD 304/2004 art. 9): por normativa esos dos solo se pueden cobrar en
 * forma de capital único, nunca como renta (ver `allowsRenta` en
 * `rescueDocsCatalog.ts`). El resto de motivos permiten puntual o renta con
 * independencia de si cortan aportaciones o no.
 *
 * Calculadora de rescate: no es un paso propio ni otro modal — es una vista
 * alternativa DENTRO del formulario (`showCalculator`). Al pulsar "Calcular
 * mi rescate", el formulario se sustituye por la calculadora a pantalla
 * completa (mismo ancho que el resto del diálogo, sin recortarla en una
 * columna — eso desbordaba) con un botón para volver a la configuración sin
 * perder lo ya rellenado.
 */

interface SolicitarRescatePorMotivoDialogV2Props {
	isOpen: boolean;
	onClose: () => void;
	userEmail: string;
	// Saldo disponible para rescatar — acota el slider de importe puntual y el
	// de renta mensual (mismos topes que `RescueRequestDialogV2` /
	// `ConfigurarRentaMensualDialogV2`).
	availableAmount: number;
	// Motivo ya activo, si lo hay — precarga el modal en modo "cambiar motivo"
	// en vez de "solicitar nuevo".
	currentReason?: string | null;
	// Un plan individual no tiene empresa que aporte — el aviso de que se deja
	// de poder aportar no debe mencionarla en ese caso.
	isIndividual?: boolean;
	// Plan del que se rescata (`user_epp_config.id`) y token: sin ellos no se
	// puede registrar la solicitud en backend.
	userEppConfigId?: string | null;
	token?: string | null;
	onSubmitted?: (reason: string) => void;
}

const MIN_AMOUNT = 100;

type RescueType = 'puntual' | 'renta';

const SolicitarRescatePorMotivoDialogV2: React.FC<SolicitarRescatePorMotivoDialogV2Props> = ({
	isOpen,
	onClose,
	userEmail,
	availableAmount,
	currentReason,
	isIndividual = false,
	userEppConfigId,
	token,
	onSubmitted
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const [step, setStep] = useState<'form' | 'submitted'>('form');
	const [reason, setReason] = useState(currentReason || 'retirement');

	// Configuración del rescate — tipo y sus campos específicos.
	const [rescueType, setRescueType] = useState<RescueType>('puntual');
	// Valor de partida del slider de importe puntual: 5% del disponible (no el
	// 100%) — un primer rescate rara vez es "vaciar el plan entero", así que
	// arrancar ahí de golpe exageraba el importe por defecto. Redondeado al
	// step del slider (50€) y sin bajar del mínimo.
	const defaultAmount = Math.min(
		Math.max(Math.round((availableAmount * 0.05) / 50) * 50, MIN_AMOUNT),
		Math.max(availableAmount, MIN_AMOUNT)
	);
	const [amount, setAmount] = useState(defaultAmount);
	// Controla la animación de invitación del slider de importe: se apaga en
	// cuanto la persona lo toca por primera vez, no solo cuando termina sola.
	const [hasAdjustedAmount, setHasAdjustedAmount] = useState(false);
	const rentaSliderMax = Math.max(
		Math.ceil(availableAmount / 12 / 100) * 100,
		MIN_MONTHLY_RENTA * 2
	);
	const defaultMonthlyAmount = Math.min(
		Math.max(Math.round(availableAmount / 60 / 10) * 10, MIN_MONTHLY_RENTA),
		rentaSliderMax
	);
	const [monthlyAmount, setMonthlyAmount] = useState(defaultMonthlyAmount);
	// Calculadora de rescate — vista alternativa DENTRO del formulario (no un
	// paso propio, no otro modal): primera vez que esta persona rescata, así
	// que le viene bien decidir importe/tipo con el impacto fiscal delante.
	const [showCalculator, setShowCalculator] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	// Cada vez que se abre, precarga el motivo actual (si lo hay) en vez de
	// arrastrar el estado de una apertura anterior. Solo depende de `isOpen`
	// (no de currentReason/availableAmount): handleSubmit llama a
	// onSubmitted?.(reason), que en el padre actualiza el propio currentReason
	// que se nos pasa — si este efecto dependiera de él, se volvía a disparar
	// justo después de enviar y deshacía el setStep('submitted') sin que se
	// llegara a ver la confirmación.
	useEffect(() => {
		if (isOpen) {
			setStep('form');
			setReason(currentReason || 'retirement');
			setRescueType('puntual');
			setAmount(defaultAmount);
			setHasAdjustedAmount(false);
			setMonthlyAmount(defaultMonthlyAmount);
			setShowCalculator(false);
			setSubmitError(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const selectedReason = RESCUE_REASONS.find((r) => r.value === reason);
	const isChangingReason = !!currentReason;
	// Antigüedad (ago-2026): a diferencia del resto de motivos, no tenemos hoy
	// el dato de cuánto es rescatable por antigüedad para casi nadie (ver
	// rescueDocsCatalog.ts, RESCUE_REASONS) — pedir un importe sobre un tope
	// que no conocemos no tiene sentido, así que se oculta el slider y se
	// informa de que la respuesta llega tras enviar la solicitud (mismo
	// mensaje que la card "Rescates" de Movimientos).
	const isSeniority = reason === 'seniority';

	// Si el motivo elegido no permite renta (enfermedad grave, desempleo de
	// larga duración), forzamos "puntual" — nunca dejar seleccionada una
	// opción que la normativa no permite para ese motivo.
	useEffect(() => {
		if (!selectedReason?.allowsRenta && rescueType === 'renta') {
			setRescueType('puntual');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reason]);

	const handleClose = () => {
		onClose();
		setStep('form');
	};

	// Se registra la solicitud en backend y se pasa a la confirmación.
	//
	// Sólo se avanza si el backend la acepta: puede rechazarla por reglas que el
	// front no replica (ya hay una solicitud viva en ese plan, o el motivo sólo
	// admite capital único por normativa). Dar por buena la petición sin
	// comprobarlo dejaría al usuario creyendo que la ha enviado.
	const handleSubmit = async () => {
		if (!token || !userEppConfigId) {
			setSubmitError(translate('rescue.solicitarDialog.errorNoPlan'));
			return;
		}

		setSubmitting(true);
		setSubmitError(null);

		try {
			await createWithdrawalRequest(token, {
				userEppConfigId,
				reason: reason as WithdrawalReason,
				...(rescueType === 'renta'
					? { kind: WithdrawalKind.ANNUITY_START, monthlyAmount }
					: { kind: WithdrawalKind.PUNCTUAL, ...(isSeniority ? {} : { amount }) })
			});

			onSubmitted?.(reason);
			setStep('submitted');
		} catch (caught) {
			setSubmitError(translateWithdrawalError(caught, translate));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto thin-scrollbar">
				<div className="individual-v2 -m-6 p-6 rounded-lg">
					<DialogHeader>
						<DialogTitle className="font-heading">
							{isChangingReason
								? translate('rescue.solicitarDialog.titleChange')
								: translate('rescue.solicitarDialog.titleNew')}
						</DialogTitle>
						<DialogDescription>
							{step === 'submitted'
								? translate('rescue.solicitarDialog.descSubmitted')
								: translate('rescue.solicitarDialog.descForm')}
						</DialogDescription>
					</DialogHeader>

					{step === 'submitted' ? (
						<RescueRequestSubmittedV2 onClose={handleClose} />
					) : showCalculator ? (
						<div id="rescate-calculadora">
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="gap-1.5 -ml-2 mb-2"
								onClick={() => setShowCalculator(false)}
							>
								<ArrowLeft className="w-4 h-4" aria-hidden="true" />
								{translate('rescue.solicitarDialog.backToConfig')}
							</Button>
							<RescueTaxSimulatorV2 initialAvailable={availableAmount} />
						</div>
					) : (
						<div className="space-y-6 w-full">
							<div className="space-y-1.5">
								<Label htmlFor="rescate-motivo">
									{translate('rescue.solicitarDialog.reasonLabel')}
								</Label>
								{isChangingReason && (
									<p className="text-xs text-muted-foreground">
										{translate('rescue.solicitarDialog.reasonHelperChanging')}
									</p>
								)}
								<Select value={reason} onValueChange={(v) => setReason(v)}>
									<SelectTrigger id="rescate-motivo" className="max-w-xs">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{RESCUE_REASONS.map(({ value, labelKey }) => (
											<SelectItem key={value} value={value}>
												{translate(labelKey)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-2">
								<div>
									<Label id="rescate-tipo-label" className="text-base">
										{translate('rescue.solicitarDialog.typeLabel')}
									</Label>
									<p className="text-xs text-muted-foreground mt-0.5">
										{translate('rescue.solicitarDialog.typeHelper')}
									</p>
								</div>
								<RadioGroup
									value={rescueType}
									onValueChange={(v) => setRescueType(v as RescueType)}
									aria-labelledby="rescate-tipo-label"
									aria-describedby={!selectedReason?.allowsRenta ? 'rescate-tipo-nota' : undefined}
									className="grid grid-cols-1 sm:grid-cols-2 gap-3"
								>
									<label
										htmlFor="rescate-tipo-puntual"
										className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
											rescueType === 'puntual' ? 'border-primary bg-primary/5' : 'border-border'
										}`}
									>
										<RadioGroupItem value="puntual" id="rescate-tipo-puntual" className="mt-0.5" />
										<div>
											<p className="text-sm font-semibold text-foreground">
												{translate('rescue.solicitarDialog.puntualTitle')}
											</p>
											<p className="text-xs text-muted-foreground mt-0.5">
												{translate('rescue.solicitarDialog.puntualDesc')}
											</p>
										</div>
									</label>
									<label
										htmlFor="rescate-tipo-renta"
										className={`flex items-start gap-3 rounded-lg border p-4 transition-colors ${
											selectedReason?.allowsRenta
												? 'cursor-pointer'
												: 'cursor-not-allowed opacity-50'
										} ${rescueType === 'renta' ? 'border-primary bg-primary/5' : 'border-border'}`}
									>
										<RadioGroupItem
											value="renta"
											id="rescate-tipo-renta"
											disabled={!selectedReason?.allowsRenta}
											className="mt-0.5"
										/>
										<div>
											<p className="text-sm font-semibold text-foreground">
												{translate('rescue.solicitarDialog.rentaTitle')}
											</p>
											<p className="text-xs text-muted-foreground mt-0.5">
												{translate('rescue.solicitarDialog.rentaDesc')}
											</p>
										</div>
									</label>
								</RadioGroup>
								{!selectedReason?.allowsRenta && (
									<p id="rescate-tipo-nota" className="text-xs text-muted-foreground" role="status">
										{translate('rescue.solicitarDialog.rentaNotAllowedPrefix')}{' '}
										<span className="font-medium text-foreground">
											{selectedReason ? translate(selectedReason.labelKey) : ''}
										</span>{' '}
										{translate('rescue.solicitarDialog.rentaNotAllowedSuffix')}
									</p>
								)}
							</div>

							{/* Primera vez que esta persona rescata, así que le viene bien
								    poder revisar el impacto fiscal después de fijar un importe de
								    partida, antes de confirmar. Vista alternativa DENTRO de este
								    mismo paso (ver comentario de cabecera), no un paso ni un modal
								    aparte. Deliberadamente NO usa un círculo tipo radio (con las
								    dos cards de arriba al lado, parecía una tercera opción
								    seleccionable) — borde discontinuo + icono en vez de círculo,
								    para que se lea como sugerencia/acción, no como una opción más. */}
							<div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
								<div className="flex items-start gap-3 flex-1 min-w-0">
									<div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
										<Calculator className="w-4 h-4 text-primary" aria-hidden="true" />
									</div>
									<div>
										<p className="text-xs font-semibold text-primary uppercase tracking-wide">
											{translate('rescue.solicitarDialog.calcRecommendationLabel')}
										</p>
										<p className="text-sm font-semibold text-foreground mt-0.5">
											{translate('rescue.solicitarDialog.calcHeading')}
										</p>
										<p className="text-xs text-muted-foreground mt-0.5">
											{translate('rescue.solicitarDialog.calcBody')}
										</p>
									</div>
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="border-primary text-primary hover:bg-primary/10 shrink-0"
									onClick={() => setShowCalculator(true)}
									aria-controls="rescate-calculadora"
								>
									{translate('rescue.solicitarDialog.calcButton')}
								</Button>
							</div>

							<hr className="border-border" />

							{rescueType === 'puntual' ? (
								<div className="space-y-2.5">
									{isSeniority ? (
										<div className="rounded-lg border border-border p-3 flex items-start gap-2">
											<Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
											<p className="text-xs text-muted-foreground">
												{translate('rescue.solicitarDialog.seniorityAmountUnknown')}
											</p>
										</div>
									) : (
										<>
											<div className="flex items-start justify-between gap-4">
												<div>
													<Label>{translate('rescue.solicitarDialog.puntualAmountLabel')}</Label>
													<p className="text-xs text-muted-foreground mt-0.5">
														{translate('rescue.solicitarDialog.availablePrefix')}{' '}
														{fmtEur(availableAmount)}
													</p>
												</div>
												<span className="text-3xl font-bold font-heading text-primary tracking-tight shrink-0">
													{fmtEur(amount)}
												</span>
											</div>
											<Slider
												className={cn(
													'slider-arca',
													!hasAdjustedAmount && '[&_[role=slider]]:animate-invite-pulse'
												)}
												value={[amount]}
												onValueChange={([v]) => {
													setAmount(v);
													setHasAdjustedAmount(true);
												}}
												min={MIN_AMOUNT}
												max={Math.max(availableAmount, MIN_AMOUNT)}
												step={50}
											/>
											<div className="flex justify-between text-xs text-muted-foreground">
												<span>
													{translate('rescue.solicitarDialog.minPrefix')} {fmtNum(MIN_AMOUNT, 0)} €
												</span>
												<span>
													{translate('rescue.solicitarDialog.maxPrefix')} {fmtEur(availableAmount)}
												</span>
											</div>
										</>
									)}
								</div>
							) : (
								<>
									<div className="space-y-2.5">
										{isSeniority ? (
											<div className="rounded-lg border border-border p-3 flex items-start gap-2">
												<Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
												<p className="text-xs text-muted-foreground">
													{translate('rescue.solicitarDialog.seniorityAmountUnknown')}
												</p>
											</div>
										) : (
											<>
												<div className="flex items-start justify-between gap-4">
													<div>
														<Label>{translate('rescue.solicitarDialog.rentaAmountLabel')}</Label>
														<p className="text-xs text-muted-foreground mt-0.5">
															{translate('rescue.solicitarDialog.availablePrefix')}{' '}
															{fmtEur(availableAmount)}
														</p>
													</div>
													<span className="text-3xl font-bold font-heading text-primary tracking-tight shrink-0">
														{fmtNum(monthlyAmount, 0)}€
													</span>
												</div>
												<Slider
													className="slider-arca"
													value={[monthlyAmount]}
													onValueChange={([v]) => setMonthlyAmount(v)}
													min={MIN_MONTHLY_RENTA}
													max={rentaSliderMax}
													step={10}
												/>
												<div className="flex justify-between text-xs text-muted-foreground">
													<span>
														{translate('rescue.solicitarDialog.minPrefix')}{' '}
														{fmtNum(MIN_MONTHLY_RENTA, 0)} €
													</span>
													<span>
														{translate('rescue.solicitarDialog.maxPrefix')}{' '}
														{fmtNum(rentaSliderMax, 0)} €
													</span>
												</div>
											</>
										)}
									</div>
									<div className="rounded-lg border border-border p-3 flex items-start gap-2">
										<Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
										<p className="text-xs text-muted-foreground">
											{translate('rescue.solicitarDialog.rentaDayInfo').replace(
												'{{day}}',
												String(RENTA_MENSUAL_DAY)
											)}
										</p>
									</div>
									{daysUntilNextRentaDay() <= RENTA_ALERT_WINDOW_DAYS && (
										<div className="rounded-lg bg-[#ffeff2] border border-[#feccd6] p-3 flex items-start gap-2">
											<AlertTriangle
												className="w-4 h-4 text-[#d4112b] mt-0.5 shrink-0"
												aria-hidden="true"
											/>
											<p className="text-xs text-[#d4112b]">
												{translate('rescue.solicitarDialog.rentaDeadlineAlert')
													.replace('{{days}}', String(RENTA_ALERT_WINDOW_DAYS))
													.replace('{{day}}', String(RENTA_MENSUAL_DAY))}
											</p>
										</div>
									)}
								</>
							)}

							{selectedReason?.stopsContributions && (
								<div className="rounded-lg bg-[#ffeff2] border border-[#feccd6] p-3 flex items-start gap-2">
									<AlertTriangle
										className="w-4 h-4 text-[#d4112b] mt-0.5 shrink-0"
										aria-hidden="true"
									/>
									<p className="text-xs text-[#d4112b]">
										{isIndividual
											? translate('rescue.solicitarDialog.stopsWarningIndividual')
											: translate('rescue.solicitarDialog.stopsWarningCompany')}
									</p>
								</div>
							)}

							{submitError && (
								<p role="alert" className="text-[13px] text-destructive text-center mb-2">
									{submitError}
								</p>
							)}
							<Button className="w-full" onClick={handleSubmit} disabled={submitting}>
								{submitting
									? translate('rescue.solicitarDialog.submitting')
									: isChangingReason
										? translate('rescue.solicitarDialog.submitChange')
										: translate('rescue.solicitarDialog.submitNew')}
							</Button>
							<RescueSupportNoteV2 />
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default SolicitarRescatePorMotivoDialogV2;
